import React, { useMemo } from 'react';

import { ClockIcon, CommentIcon, DownloadIcon } from '@primer/octicons-react';
import { StyledOcticon, Timeline } from '@primer/react';

import { formatISOAsRelative } from '../../../utils/date_utils';
import { getUserDisplayName } from '../../../utils/name_utils';
import { preserveWorkspaceURL } from '../../../utils/url_utils';

import { ACTIVITY_TYPE } from '../../../constants/wallaroo.constants';

import DividerDot from '../../common/misc/DividerDot';
import ErrorLabel from '../../common/Labels/ErrorLabel';
import Label from '../../common/Labels/Label';
import LinkButton from '../../common/Labels/LinkButton';
import LinkLabel from '../../common/Labels/LinkLabel';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import MinorLabel from '../../common/Labels/MinorLabel';

const CommentItem = ({
  comment,
  currentUser,
  editModeCommentID,
  handleEnterEditMode,
  handleDeleteComment,
  condensed = false,
}) => {
  const user = comment?.user;
  const displayName = useMemo(() => getUserDisplayName(user), [user])

  return (
    <Timeline.Item className="text-brand items-center" condensed={condensed}>
      <Timeline.Badge className="before:bg-border-dark">
        <StyledOcticon icon={CommentIcon} />
      </Timeline.Badge>
      <Timeline.Body>
        <div id={`activity-item-${comment.id}`} className="p-1">
          <div className="mt-1">
            <Label>
              {displayName}
            </Label>
            {
              comment.model ? (
                <>
                  <Label className="mx-1">commented on</Label>
                  <LinkLabel
                    to={preserveWorkspaceURL(`/models/${comment.model.modelID}/versions/${comment.model.modelVersion}`)}
                    bold
                  >
                    {comment.model.modelID} {comment.model.modelVersion}
                  </LinkLabel>
                </>
              ) : comment.pipeline ? (
                <>
                  <Label className="mx-1">commented on</Label>
                  <LinkLabel
                    to={preserveWorkspaceURL(`/pipelines/${comment.pipeline.pipelineID}/versions/${comment.pipeline.latestVersion.version}`)}
                    bold
                  >
                    {comment.pipeline.pipelineID}
                  </LinkLabel>
                </>
              ) : <Label className="ml-1">commented</Label>
            }
            :
          </div>

          <p className="ml-2 my-1">
            {comment.deleted_at ? (
              <MinorLabel>(deleted)</MinorLabel>
            ) : editModeCommentID === comment.id ? (
              <MinorLabel>Editing...</MinorLabel>
            ) : (
              <>
                {comment.message}
                {comment.created_at !== comment.updated_at && (
                  <MinorLabel> (edited)</MinorLabel>
                )}
              </>
            )}
          </p>

          <div id="comment-control-row" className="flex flex-row mb-1">
            <ErrorLabel size="sm">{formatISOAsRelative(comment.created_at)}</ErrorLabel>
            {!!currentUser && !comment.deleted_at && comment.user_id === currentUser.id && (
              <>
                <DividerDot />
                <LinkButton color="gray-500" onClick={() => handleEnterEditMode(comment.id)}>Edit</LinkButton>
                <DividerDot />
                <LinkButton color="gray-500" onClick={() => handleDeleteComment(comment.id)}>Delete</LinkButton>
              </>
            )}
          </div>
        </div>
      </Timeline.Body>
    </Timeline.Item>
  );
}

const ModelActivityItem = ({
  modelID,
  modelVersion,
  message,
  eventDate,
  condensed = false,
}) => (
  <Timeline.Item condensed={condensed}>
    <Timeline.Badge>
      <StyledOcticon icon={ClockIcon} />
    </Timeline.Badge>
    <Timeline.Body>
      <div className="p-1 flex flex-row">
        <div className="flex flex-row">
          <LinkLabel
            to={preserveWorkspaceURL(`/models/${modelID}/versions/${modelVersion}`)}
            bold
          >
            {modelID} {modelVersion}
          </LinkLabel>
          <Label className="ml-1">{message}</Label>
        </div>
        {
          eventDate ? (
            <ErrorLabel className="ml-1" size="sm">{formatISOAsRelative(eventDate)}</ErrorLabel>
          ) : null
        }
      </div>
    </Timeline.Body>
  </Timeline.Item>
);

const PipelineActivityItem = ({
  pipelineID,
  pipelineVersion,
  message,
  eventDate,
  condensed = false,
}) => (
  <Timeline.Item condensed={condensed}>
    <Timeline.Badge>
      <StyledOcticon icon={ClockIcon} />
    </Timeline.Badge>
    <Timeline.Body>
      <div className="p-1 flex flex-row">
        <div className="flex flex-row">
          <LinkLabel
            to={preserveWorkspaceURL(`/models/${pipelineID}/versions/${pipelineVersion}`)}
            bold
          >
            {pipelineID} {pipelineVersion}
          </LinkLabel>
          <Label className="ml-1">{message}</Label>
        </div>
        {
          eventDate ? (
            <ErrorLabel className="ml-1" size="sm">{formatISOAsRelative(eventDate)}</ErrorLabel>
          ) : null
        }
      </div>
    </Timeline.Body>
  </Timeline.Item>
);

export const LoadingActivityItem = ({ condensed }) => (
  <Timeline.Item condensed={condensed}>
    <Timeline.Badge>
      <StyledOcticon icon={DownloadIcon} />
    </Timeline.Badge>
    <Timeline.Body>
      <div className="p-1">
        <LoadingSpinner animationKey="activity-item-loading-spinner" />
      </div>
    </Timeline.Body>
  </Timeline.Item>
)

export const ActivityItem = ({
  entry,
  currentUser,
  editModeCommentID,
  handleEnterEditMode,
  handleDeleteComment,
  condensed = false,
}) => {
  if (entry.type === ACTIVITY_TYPE.COMMENT) {
    return (
      <CommentItem
        comment={entry.content}
        currentUser={currentUser}
        editModeCommentID={editModeCommentID}
        handleEnterEditMode={handleEnterEditMode}
        handleDeleteComment={handleDeleteComment}
        condensed={condensed}
      />
    )
  }
  if (entry.type === ACTIVITY_TYPE.MODEL) {
    return (
      <ModelActivityItem
        modelID={entry?.content?.modelID}
        modelVersion={entry?.content?.modelVersion}
        message={entry?.content?.message}
        eventDate={entry?.eventDate}
        condensed={condensed}
      />
    );
  }
  if (entry.type === ACTIVITY_TYPE.PIPELINE) {
    return (
      <PipelineActivityItem
        pipelineID={entry?.content?.pipelineID}
        pipelineVersion={entry?.content?.pipelineVersion}
        message={entry?.content?.message}
        eventDate={entry?.eventDate}
        condensed={condensed}
      />
    );
  }

  return <Timeline.Break />;
}

export default ActivityItem;
